import { StaffTransition } from '.';
import IPlayable from '../Playable';
import { ProgressState } from '../Progress';
import { Config } from './Types';
import ClefMeasure from './Measure';
import { getColorByCbType } from 'Components/Colors';
import { COLORBLIND_TYPE, ERROR_REC_CLASS } from 'Utils/Constants';

export default class RestGraphic extends Phaser.GameObjects.Container implements IPlayable {
  measureIdx: number;
  isRest = true;
  idx: number;
  resetTimer?: Phaser.Time.TimerEvent;
  isFocused: boolean = false;
  timingState?: ProgressState;
  id: string;
  tweenHandle?: Phaser.Tweens.Tween;
  restGraphic?: Phaser.GameObjects.Image;
  restRectangle?: Phaser.GameObjects.Rectangle;
  dot?: Phaser.GameObjects.Arc;
  timestamp: number;
  unscaledX: number;
  line: number;
  _line: number;
  _isQuarterRest: boolean = false;
  _timeSigNum?: number;
  _relLength: number;
  defaultColor: number;

  /** DPR used for rendering. Inherited from parent Clef. */
  renderDpr: number;

  constructor(
    scene: Phaser.Scene,
    measureIdx: number,
    id: string,
    x: number,
    unscaledX: number,
    line: number,
    length: number,
    { pitchLineGap, pitchLineWidth }: Config,
    idx: number,
    timeSigNum: number,
    timestamp: number,
    renderDpr: number = window.devicePixelRatio,
    color?: number | string,
  ) {
    super(scene);
    this.renderDpr = renderDpr;
    this.defaultColor = Phaser.Display.Color.ValueToColor(color ?? 0x000000).color;
    this._relLength = length * 4;
    this._timeSigNum = timeSigNum;
    this.unscaledX = unscaledX;
    this.timestamp = timestamp;
    this.setX(x);
    scene.add.existing(this);
    this.measureIdx = measureIdx;
    let lengthToRestTexture = '';
    let dotted = false;
    this.line = line;
    console.debug('rest length', timeSigNum, length);
    if (length === timeSigNum * 0.25) {
      lengthToRestTexture = 'whole_rest';
    } else {
      switch (length) {
        case 0.25:
          lengthToRestTexture = 'quarter_rest';
          this._isQuarterRest = true;
          break;
        case 0.3125:
          lengthToRestTexture = 'quarter_rest';
          dotted = true;
          this._isQuarterRest = true;
          break;
        case 0.5:
          lengthToRestTexture = 'half_rest';
          break;
        case 0.75:
          lengthToRestTexture = 'half_rest';
          dotted = true;
          break;
        case 1:
          lengthToRestTexture = 'whole_rest';
          break;
        case 1.5:
          lengthToRestTexture = 'whole_rest';
          dotted = true;
          break;
        default:
          console.error('Only half, quarter and whole rests supported for now!');
      }
    }
    const l = line / 2 - (lengthToRestTexture === 'whole_rest' ? 1 : 0);
    this._line = l;
    const y = l * pitchLineGap;
    let desiredHeight = 0;
    switch (length) {
      case 0.25:
        desiredHeight = 3 * pitchLineGap;
        break;
      default:
        desiredHeight = 0.6 * pitchLineGap;
        break;
    }
    //super(scene, x, y, lengthToRestTexture);
    let graphic: Phaser.GameObjects.Image | Phaser.GameObjects.Rectangle;
    if (lengthToRestTexture === 'quarter_rest') {
      this.restGraphic = scene.add.image(0, y, lengthToRestTexture);
      graphic = this.restGraphic;
    } else {
      //const halfRestOffset = (length === 0.5 && length !== timeSigNum * 0.25) ? 2 : 0;
      this.restRectangle = scene.add.rectangle(0, y, 50, 20, 0, 1);
      graphic = this.restRectangle;
    }
    this.add(graphic);
    const scale = desiredHeight / graphic.height;
    graphic.setScale(scale);
    if (dotted) {
      const dotX = graphic.x + graphic.displayWidth * 1.2;
      let yOffset = 0;
      const dotR = graphic.displayWidth / 6;
      if (line % 2 === 0) {
        yOffset = graphic.displayHeight / 3 + dotR / 2;
      }
      const dotY = graphic.y - yOffset;
      this.dot = scene.add.circle(dotX, dotY, dotR, this.defaultColor, 1);
      this.add(this.dot);
    }
    if (length === 0.5 && length !== timeSigNum * 0.25) {
      this.restRectangle!.setOrigin(0.5, 1);
      this.restRectangle!.y += pitchLineWidth;
    } else if (length === 1 || length === timeSigNum * 0.25) {
      this.restRectangle!.setOrigin(0.5, 0);
    }
    this.id = id;
    this.idx = idx;
    this.setTintFill(this.defaultColor);
    this.restRectangle?.setAlpha(0);
    this.restGraphic?.setAlpha(0);
    this.dot?.setAlpha(0);
  }

  setParent(parent: ClefMeasure) {
    parent.add(this);
  }

  getY() {
    return this.y;
  }

  update(_: number) {}

  destroy() {
    //this.resetTimer?.remove();
    this.tweenHandle?.remove();
    this.tweenHandle = undefined;
    this.isFocused = false;
    super.destroy();
  }

  setFocus(focus: boolean) {
    this.isFocused = focus;
  }

  transitionOut() {
    this.addTween({
      targets: [this.restRectangle, this.restGraphic, this.dot],
      alpha: 0,
      duration: 500,
    });
  }

  transition(style: StaffTransition, _idx: number) {
    switch (style) {
      case 'Draw': {
        const props = {
          rectScale: this.restRectangle?.scale,
          graphicScale: this.restGraphic?.scale,
          dotScale: this.dot?.scale,
        };
        this.restGraphic?.setScale(0);
        this.restRectangle?.setScale(0);
        this.dot?.setScale(0);
        const onComplete = () => {
          if (this.dot)
            this.addTween({
              targets: [this.dot],
              scale: props.dotScale,
              alpha: 1,
              duration: 200,
            });
        };
        if (this.restRectangle)
          this.addTween({
            targets: [this.restRectangle],
            alpha: 1,
            scale: props.rectScale,
            duration: 300,
            onComplete,
          });
        if (this.restGraphic)
          this.addTween({
            targets: [this.restGraphic],
            alpha: 1,
            scale: props.graphicScale,
            duration: 300,
            onComplete,
          });
        break;
      }
      case 'None': {
        this.restRectangle?.setAlpha(1);
        this.restGraphic?.setAlpha(1);
        this.dot?.setAlpha(1);
        break;
      }
      case 'Fade': {
        this.addTween({
          targets: [this.restRectangle, this.restGraphic, this.dot],
          alpha: 1,
          duration: 500,
        });
        break;
      }
    }
  }

  addTween(tween: Phaser.Tweens.Tween | Phaser.Types.Tweens.TweenBuilderConfig) {
    this.tweenHandle = this.scene.tweens.add(tween);
  }

  setTimingState(state: ProgressState, colorblindType?: COLORBLIND_TYPE | null) {
    this.timingState = state;
    switch (state) {
      case ProgressState.LateStart:
      case ProgressState.MistimedRelease: {
        // Use colorblind-aware color for semi-error (yellow/orange/grey)
        const semiErrorColor = getColorByCbType(ERROR_REC_CLASS.SEMI_ERROR, colorblindType ?? null);
        this.setTintFill(semiErrorColor);
        break;
      }
      case ProgressState.Ended: {
        // Use colorblind-aware color for correct (green/blue/white)
        const correctColor = getColorByCbType(ERROR_REC_CLASS.CORRECT, colorblindType ?? null);
        this.setTintFill(correctColor);
        break;
      }
      case ProgressState.Missed: {
        // Use colorblind-aware color for error (red/brown/black)
        const errorColor = getColorByCbType(ERROR_REC_CLASS.ERROR, colorblindType ?? null);
        this.setTintFill(errorColor);
        break;
      }
      default:
    }
  }

  setTintFill(color: number | string) {
    const col = Phaser.Display.Color.ValueToColor(color).color;
    this.restGraphic?.setTintFill(col);
    this.restRectangle?.setFillStyle(col);
    this.dot?.setFillStyle(col);
  }

  getState() {
    return this.timingState;
  }

  shake() {}

  reset(force: boolean) {
    this.timingState = undefined;
    const delay = force ? 0 : 2000 + this.idx * 100;
    this.resetTimer = this.scene.time.delayedCall(
      delay,
      () => {
        this.setTintFill(this.defaultColor);
      },
      [],
      this,
    );
  }

  showFingering(_parent: Phaser.GameObjects.Container, _max: number, _min: number) {}

  getPosition() {
    return {
      x: this.restGraphic?.x ?? this.restRectangle!.x,
      y: this.restGraphic?.y ?? this.restRectangle!.y,
    };
  }

  getHaloOffset() {
    return 0;
  }

  setColor(color: number | string) {
    this.setTintFill(color);
    //this.graphic.setTintFill(color);
  }

  resetColor() {
    this.setTintFill(this.defaultColor);
  }

  resize(pitchLineGap: number, xPosScale: number, fullMeasureWidth: number, gap: number) {
    const graphic = this.restGraphic ?? this.restRectangle!;
    this.setX(this.unscaledX * xPosScale);
    //graphic.setX(this.unscaledX * xPosScale);
    graphic.setY(this._line * pitchLineGap * this.renderDpr);
    const desiredHeight = this._isQuarterRest
      ? 3 * pitchLineGap * this.renderDpr
      : 0.6 * pitchLineGap * this.renderDpr;
    const scale = desiredHeight / graphic.height;
    graphic?.setScale(scale);
    console.debug('rest', this._relLength, this._timeSigNum);
    if (this._timeSigNum === this._relLength) {
      this.setX(fullMeasureWidth / 2 - gap);
      //graphic.setX(fullMeasureWidth);
    }
    const dotR = graphic.displayWidth / 6;
    this.dot?.setRadius(dotR);
    const dotX = graphic.x + graphic.displayWidth * 1.2;
    let yOffset = 0;
    if (this.line % 2 === 0) {
      yOffset = graphic.displayHeight / 3 + dotR / 2;
    }
    const dotY = graphic.y - yOffset;
    this.dot?.setPosition(dotX, dotY);
  }

  setAlpha(value?: number): this {
    this.restGraphic?.setAlpha(value);
    this.restRectangle?.setAlpha(value);
    this.dot?.setAlpha(value);
    return this;
  }

  getBoundsY() {
    return this.getBounds().y + this.getBounds().height / 2;
  }

  pulse() {}
}
